import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { ApiError, fetchJson } from "@/lib/api";
import type { CategoryDetail, CategorySummary, TypeDetail, TypeSummary } from "./types";

// react-query hooks for the public catalog read endpoints. Cache for
// 30s (the QueryClient default in entrypoints/customer.tsx) is enough
// for a single browse session; cache invalidation only matters when
// admin renames a slug — handled by the 301 redirect on the next fetch.

export const categoriesKey = ["public", "categories"] as const;
export const categoryKey = (slug: string) =>
  ["public", "categories", slug] as const;
export const typeKey = (slug: string) => ["public", "types", slug] as const;
export const typesSearchKey = (query: string) =>
  ["public", "types", "search", query] as const;
export const typesByIdsKey = (ids: number[]) =>
  ["public", "types", "by_ids", [...ids].sort((a, b) => a - b)] as const;

export function usePublicCategories(): UseQueryResult<CategorySummary[], ApiError | Error> {
  return useQuery({
    queryKey: categoriesKey,
    queryFn: async () => {
      const res = await fetchJson<{ categories: CategorySummary[] }>("/api/v1/categories");
      return res.categories;
    }
  });
}

export function usePublicCategory(
  slug: string | null
): UseQueryResult<CategoryDetail, ApiError | Error> {
  return useQuery({
    queryKey: slug ? categoryKey(slug) : ["public", "categories", "none"],
    queryFn: async () => {
      const res = await fetchJson<{ category: CategoryDetail }>(
        `/api/v1/categories/${slug}`
      );
      return res.category;
    },
    enabled: slug != null
  });
}

export function usePublicType(
  slug: string | null
): UseQueryResult<TypeDetail, ApiError | Error> {
  return useQuery({
    queryKey: slug ? typeKey(slug) : ["public", "types", "none"],
    queryFn: async () => {
      const res = await fetchJson<{ type: TypeDetail }>(`/api/v1/types/${slug}`);
      return res.type;
    },
    enabled: slug != null
  });
}

// Bulk lookup by id — used by the cart page (which stores type_ids
// only, no slugs). Returns TypeSummary, not TypeDetail, so the cart
// can render names + thumbnails + base prices without pulling the
// full attachments/photos/specs payload for every line.
export function usePublicTypesByIds(
  ids: number[]
): UseQueryResult<TypeSummary[], ApiError | Error> {
  return useQuery({
    queryKey: typesByIdsKey(ids),
    queryFn: async () => {
      const qs = ids.map((id) => `ids[]=${id}`).join("&");
      const res = await fetchJson<{ types: TypeSummary[] }>(`/api/v1/types?${qs}`);
      return res.types;
    },
    enabled: ids.length > 0
  });
}

// FR-5 — search across category + type names. Server uses LIKE; this
// hook is consumed by both the header type-ahead dropdown (caller
// slices to 8 results) and the full /search results page.
export function usePublicTypeSearch(
  query: string,
  options: { enabled?: boolean | undefined } = {}
): UseQueryResult<TypeSummary[], ApiError | Error> {
  const trimmed = query.trim();
  return useQuery({
    queryKey: typesSearchKey(trimmed),
    queryFn: async () => {
      const res = await fetchJson<{ types: TypeSummary[] }>(
        `/api/v1/types?query=${encodeURIComponent(trimmed)}`
      );
      return res.types;
    },
    enabled: (options.enabled ?? true) && trimmed.length > 0
  });
}

// Phase 4r — full unfiltered catalog. Used by the search-results
// "Did you mean…" suggestion (computes Levenshtein distance against
// every type name client-side) and lazily fired only when the search
// returns zero matches, so the cost is bounded to the cohort of
// customers who actually mistype. Cached on the same query key as
// "search with empty query" so a future no-query search reuses the
// payload.
export const allTypesKey = ["public", "types", "all"] as const;

export function useAllPublicTypes(
  options: { enabled?: boolean | undefined } = {}
): UseQueryResult<TypeSummary[], ApiError | Error> {
  return useQuery({
    queryKey: allTypesKey,
    queryFn: async () => {
      const res = await fetchJson<{ types: TypeSummary[] }>("/api/v1/types");
      return res.types;
    },
    enabled: options.enabled ?? true
  });
}
